<template>
  <div
    class="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-screen bg-background"
  >
    <div class="grid grid-cols-1 lg:grid-cols-4 gap-8">
      <!-- Left Sidebar (Same as JobsView) -->
      <div
        class="hidden lg:block lg:col-span-1 space-y-8 h-[calc(100vh-80px)] sticky top-20"
      >
        <div
          class="bg-card rounded-[20px] p-6 shadow-sm border border-border flex flex-col h-full"
        >
          <!-- Following Section -->
          <div class="mb-10 flex-shrink-0">
            <h3 class="font-bold text-[16px] text-foreground mb-4">
              Following
            </h3>
            <div class="space-y-4">
              <div
                v-for="person in followingList"
                :key="person.id"
                class="flex items-center justify-between"
              >
                <div class="flex items-center space-x-3">
                  <img
                    :src="person.avatar"
                    alt="Avatar"
                    class="w-[42px] h-[42px] rounded-full object-cover"
                  />
                  <div>
                    <h4
                      class="font-bold text-[14px] leading-tight text-foreground"
                    >
                      {{ person.name }}
                    </h4>
                    <p class="text-[12px] text-muted-foreground mt-0.5">
                      {{ person.title }}
                    </p>
                  </div>
                </div>
                <button
                  class="px-4 py-1.5 border border-border/80 text-foreground text-[12px] font-medium rounded-full hover:bg-muted/30 transition-colors"
                >
                  Unfollow
                </button>
              </div>
            </div>
          </div>

          <!-- People to Follow Section -->
          <div class="flex-shrink-0">
            <h3 class="font-bold text-[16px] text-foreground mb-4">
              People To Follow
            </h3>
            <div class="space-y-4">
              <div
                v-for="person in suggestedUsers"
                :key="person.id"
                class="flex items-center justify-between"
              >
                <div class="flex items-center space-x-3">
                  <img
                    :src="person.avatar"
                    alt="Avatar"
                    class="w-[42px] h-[42px] rounded-full object-cover"
                  />
                  <div>
                    <h4
                      class="font-bold text-[14px] leading-tight text-foreground"
                    >
                      {{ person.name }}
                    </h4>
                    <p class="text-[12px] text-muted-foreground mt-0.5">
                      {{ person.title }}
                    </p>
                  </div>
                </div>
                <button
                  class="px-4 py-1.5 bg-primary text-primary-foreground text-[12px] font-medium rounded-full hover:bg-primary/90 transition-colors"
                >
                  Follow
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="col-span-1 lg:col-span-3">
        <!-- Header Controls -->
        <div class="flex items-center justify-between mb-12">
          <button
            @click="$router.back()"
            class="flex items-center text-muted-foreground hover:text-foreground font-medium text-[15px] group"
          >
            <div
              class="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center mr-3 group-hover:scale-105 transition-transform"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </div>
            Back
          </button>

          <div class="flex justify-center flex-1">
            <div class="relative">
              <span class="text-[17px] font-semibold text-foreground pb-2 px-1"
                >Jobs</span
              >
              <div
                class="absolute bottom-0 left-0 w-full h-0.5 bg-foreground rounded-full"
              ></div>
            </div>
          </div>

          <div class="w-24"></div>
          <!-- Empty div for flex balance -->
        </div>

        <!-- Job Details Split Layout -->
        <div v-if="loading" class="flex flex-col lg:flex-row gap-10">
          <div class="flex-1 lg:w-2/3 space-y-8">
            <SkeletonLoader width="300px" height="40px" />
            <div class="space-y-4">
              <SkeletonLoader width="150px" height="24px" />
              <SkeletonLoader width="100%" height="100px" />
            </div>
            <div class="space-y-4">
              <SkeletonLoader width="200px" height="24px" />
              <SkeletonLoader width="100%" height="150px" />
            </div>
          </div>
          <div class="lg:w-1/3">
            <SkeletonLoader width="100%" height="400px" class="rounded-3xl" />
          </div>
        </div>
        <div v-else class="flex flex-col lg:flex-row gap-10">
          <!-- Left Content: Job Description -->
          <div class="flex-1 lg:w-2/3 space-y-8">
            <h1
              class="text-[36px] font-semibold text-foreground tracking-tight"
            >
              {{ jobDetails.title }}
            </h1>

            <section class="space-y-3">
              <h2 class="text-[20px] font-semibold text-foreground">
                Who we are
              </h2>
              <p
                class="text-[15px] leading-relaxed text-muted-foreground whitespace-pre-line"
              >
                {{ jobDetails.whoWeAre }}
              </p>
            </section>

            <section
              v-if="jobDetails.whatWeAreLookingFor.bullets.length > 0"
              class="space-y-4"
            >
              <h2 class="text-[20px] font-semibold text-foreground">
                What we're looking for
              </h2>
              <ul
                class="list-disc pl-5 space-y-2 text-[15px] leading-relaxed text-muted-foreground"
              >
                <li
                  v-for="(item, index) in jobDetails.whatWeAreLookingFor
                    .bullets"
                  :key="index"
                >
                  {{ item }}
                </li>
              </ul>
            </section>

            <div class="pt-6">
              <button
                @click="applyNow"
                class="px-8 py-3.5 bg-primary text-primary-foreground text-[15px] font-medium rounded-full hover:bg-primary/90 transition-colors w-max"
              >
                Apply for this role
              </button>
            </div>
          </div>

          <!-- Right Sidebar: Context Card -->
          <div class="lg:w-1/3">
            <div
              class="bg-card border border-border rounded-3xl p-8 sticky top-24 shadow-sm flex flex-col items-center text-center"
            >
              <!-- Company Logo -->
              <div
                class="w-20 h-20 bg-muted rounded-2xl flex items-center justify-center mb-5"
              >
                <svg
                  class="w-8 h-8 text-white/80"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"
                  />
                </svg>
              </div>

              <!-- Company Name -->
              <h2 class="text-[24px] font-semibold text-foreground mb-2">
                {{ jobDetails.company }}
              </h2>

              <!-- Website Link -->
              <a
                v-if="
                  jobDetails.application_link &&
                  jobDetails.application_link !== '#'
                "
                :href="jobDetails.application_link"
                target="_blank"
                class="text-[13px] text-muted-foreground font-medium hover:text-primary transition-colors mb-6 pb-0.5 border-b border-muted-foreground/30 hover:border-primary"
              >
                Visit Website
              </a>

              <!-- Main Apply Button -->
              <button
                @click="applyNow"
                class="w-full py-3.5 bg-primary text-primary-foreground text-[15px] font-medium rounded-full hover:bg-primary/90 transition-colors mb-10"
              >
                Apply for this role
              </button>

              <!-- Meta Information Blocks -->
              <div
                class="w-full space-y-6 text-left border-t border-border pt-8"
              >
                <div>
                  <p
                    class="text-[13px] text-muted-foreground/70 font-medium mb-1"
                  >
                    Job type
                  </p>
                  <p class="text-[15px] font-semibold text-foreground">
                    {{ jobDetails.type }}
                  </p>
                </div>

                <div>
                  <p
                    class="text-[13px] text-muted-foreground/70 font-medium mb-1"
                  >
                    Location
                  </p>
                  <p class="text-[15px] font-semibold text-foreground">
                    {{ jobDetails.location }}
                  </p>
                </div>

                <div>
                  <p
                    class="text-[13px] text-muted-foreground/70 font-medium mb-1"
                  >
                    Date posted
                  </p>
                  <p class="text-[15px] font-semibold text-foreground">
                    {{ jobDetails.datePosted }}
                  </p>
                </div>
                <div>
                  <p
                    class="text-[13px] text-muted-foreground/70 font-medium mb-1"
                  >
                    Deadline
                  </p>
                  <p class="text-[15px] font-semibold text-foreground">
                    {{ jobDetails.deadline }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import SkeletonLoader from "@/components/SkeletonLoader.vue";

export default {
  name: "JobDetailsView",
  components: {
    SkeletonLoader,
  },
  data() {
    return {
      loading: true,
      jobDetails: {
        title: "",
        company: "",
        whoWeAre: "",
        whatWeAreLookingFor: {
          intro: "",
          bullets: [],
        },
        type: "",
        location: "",
        datePosted: "",
        // deadline: "",
        application_link: "#",
      },
      // Mock tracking lists (keeping as they are for design)
      followingList: [
        {
          id: 1,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 2,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 3,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 4,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
      ],
      suggestedUsers: [
        {
          id: 5,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 6,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 7,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
        {
          id: 8,
          name: "Lisa Balogun",
          title: "Aspiring Product Designer",
          avatar: require("@/assets/users/default-profile-picture.jpg"),
        },
      ],
    };
  },
  methods: {
    async fetchJobDetails() {
      this.loading = true;
      const jobId = this.$route.params.jobId || this.$route.params.id;
      try {
        const response = await apiClient.get(`/web/jobs/${jobId}`);
        if (response.data.status_code === 200) {
          const data = response.data.data;
          this.jobDetails = {
            title: data.title,
            company: data.company,
            whoWeAre: data.description, // Reusing description for company context if separate field missing
            whatWeAreLookingFor: {
              intro: data.description,
              bullets: data.responsibilities || [],
            },
            type:
              data.job_type.replace("_", " ").charAt(0).toUpperCase() +
              data.job_type.replace("_", " ").slice(1),
            location: data.location,
            datePosted: new Date(data.created_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            }),
            deadline: new Date(data.application_deadline).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            }),
            application_link: data.application_link,
          };
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
      } finally {
        this.loading = false;
      }
    },
    applyNow() {
      if (
        this.jobDetails.application_link &&
        this.jobDetails.application_link !== "#"
      ) {
        window.open(this.jobDetails.application_link, "_blank");
      } else {
        alert("Application link not available for this role.");
      }
    },
  },
  mounted() {
    window.scrollTo(0, 0);
    this.fetchJobDetails();
  },
};
</script>

<style scoped>
/* Scoped overrides if necessary */
</style>
